import { useMutation, useQueryClient } from '@tanstack/react-query'
import { type FormEvent, useState } from 'react'
import { SUBMIT_ERROR_FALLBACK, toUserMessage } from '../lib/error-copy.js'
import { TRASH_ACTION } from '../lib/labels.js'
import { deleteProject } from '../lib/projects-api.js'
import { useToast } from '../lib/toast.js'
import { ModalOverlay } from './ModalOverlay.js'

type Props = {
  open: boolean
  onClose: () => void
  project: { id: string; name: string } | null
  /** 削除成功後に呼ばれる（一覧画面に戻す等の用途） */
  onDeleted?: () => void
}

export function DeleteProjectModal({ open, onClose, project, onDeleted }: Props) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [confirmation, setConfirmation] = useState('')
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: ({ id, confirmation }: { id: string; confirmation: string }) =>
      deleteProject(id, confirmation),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      toast.success(`プロジェクト「${project?.name}」を${TRASH_ACTION.toastPurgedProject}`)
      setConfirmation('')
      setError(null)
      onClose()
      onDeleted?.()
    },
    onError: (err) => {
      setError(toUserMessage(err, SUBMIT_ERROR_FALLBACK))
    },
  })

  if (!open || !project) return null

  const matches = confirmation === project.name

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    if (!matches) {
      setError('プロジェクト名が一致しません')
      return
    }
    mutation.mutate({ id: project.id, confirmation })
  }

  return (
    <ModalOverlay className="bg-black/50" onBackdropClick={onClose} onEscape={onClose}>
      <div
        className="bg-background border border-border rounded-lg shadow-lg p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="presentation"
      >
        <h2 className="text-lg font-semibold text-destructive">プロジェクトを完全に消しますか？</h2>
        <div className="mt-4 space-y-3">
          <div className="text-sm bg-destructive/5 border border-destructive/30 rounded-md p-3 space-y-1">
            <p className="font-medium text-destructive">この操作は取り消せません</p>
            <p className="text-foreground/80">
              中のファイル・共有URL・アクセスログがすべて消え、元に戻せません。
            </p>
          </div>
          <p className="text-sm text-muted-foreground">
            完全に消すには、プロジェクト名{' '}
            <strong className="font-mono text-foreground">{project.name}</strong>{' '}
            を入力してください。
          </p>
          <form onSubmit={onSubmit} className="space-y-3">
            <input
              type="text"
              // biome-ignore lint/a11y/noAutofocus: モーダル内のメイン入力なので OK
              autoFocus
              required
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md bg-background font-mono"
              placeholder={project.name}
            />
            {error && (
              <div className="text-sm text-destructive bg-destructive/5 border border-destructive/30 rounded-md p-2">
                {error}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={mutation.isPending}
                className="px-4 py-2 border border-border rounded-md font-medium"
              >
                キャンセル
              </button>
              <button
                type="submit"
                disabled={mutation.isPending || !matches}
                className="px-4 py-2 bg-destructive text-destructive-foreground rounded-md font-medium disabled:opacity-50"
              >
                {mutation.isPending ? '削除中…' : TRASH_ACTION.purgeNow}
              </button>
            </div>
          </form>
        </div>
      </div>
    </ModalOverlay>
  )
}
